/**
 * This module allows to use the Google Maps from different, independant
 * places in the code, without having to rely on a single initMap callback.
 *
 * Usage:
 * 1. Add the Google Maps API script specifying `callback=Promutuel.gmaps.initMap`.
 * 2. When you need to use the API, simply add a call to
 *    `await Promutuel.gmaps.initialize()` before using the `google.maps` object.
 */

const target = new EventTarget();
target.isLoaded = false;

Promutuel.gmaps = {
  // Function to be called to wait for Google Maps to be ready.
  initialize: /*async*/ function() {
    return new Promise(resolve => {
      if (target.isLoaded) resolve();
      else target.addEventListener('load', resolve);
    });
  },
  // This is the `initMap` callback for the Google Maps script.
  // Don't call this function from user code.
  initMap: function() {
    target.isLoaded = true;
    target.dispatchEvent(new CustomEvent('load'));
  },

  /** Returns the default options for Promutuel's custom-styled maps. */
  getDefaultMapOptions: function() {
    return {
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      center: { lat: 47.1, lng: -71.2 },
      zoom: 6,
      mapTypeControl: false, // Remove Plan/Satellite/Hybrid/Terrain selector.
      streetViewControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      styles: [
        {
          stylers: [{
            saturation: -100,
          }]
        },
        {
          // Remove points of interest.
          featureType: "poi",
          elementType: "labels",
          stylers: [{
            visibility: "off"
          }]
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{
            color: "#ebd9b8"
          }]
        },
        {
          elementType: "labels.text.stroke",
          stylers: [{
            visibility: "off"
          }]
        },
        {
          elementType: "labels.text.fill",
          stylers: [{
            color: "#908f8C"
          }]
        }
      ],
    };
  },

  /**
   * Centers and zooms a map around a set of markers.
   *
   * @param {google.maps.Marker[]} markers A list of markers.
   *
   * All markers should be attached to the same map.
   * (The operation is performed on the first one.)
   */
  fitMarkersIntoView: function(markers) {
    if (!markers || !markers.length) return;

    const map = markers[0].map;

    if (markers.length > 1) {
      const zone = new google.maps.LatLngBounds();

      markers.forEach(marker => {
        zone.extend(marker.position); // Add the point to the zone.
      });

      // Center and zoom onto the zone, if it's not empty..
        map.fitBounds(zone);
    }
    // If there is only one marker, limit the zoom level to display a large enough area around.
    else if (markers.length == 1) {
      map.setCenter(markers[0].position);
      map.setZoom(11);
    }
  },

  /**
   * Convert an object with `latitude` and `longitude` properties
   * to a GMaps-compatible LatLngLiteral.
   *
   * If the object is already a LatLngLiteral, its properties will be kept.
   */
  toLatLng: function (point) {
    return {
      lat: parseFloat(point.lat ?? point.latitude),
      lng: parseFloat(point.lng ?? point.longitude),
    };
  },

}
